import { Create, useStepsForm, useSelect } from "@refinedev/antd";

// It is recommended to use explicit import as seen below to reduce bundle size.
// import { IconName } from "@ant-design/icons";
import * as Icons from "@ant-design/icons";

import { Button, Divider, Form, Select, Steps, Tag, Alert, Result } from "antd";
import { IResourceComponentsProps, useApiUrl, useNotification, useNavigation } from "@refinedev/core";
import axios from "axios";
import FileSaver from "file-saver";

import { IVesselResource, IVessel, IHoldingsReportEntry } from "interfaces";
import React from "react";
import { generateHoldingsPdf } from "./utils/generate-holdings-pdf";

const TOKEN_KEY = "emr-auth";

const getAuthHeaders = () => ({
  headers: {
    Authorization: `Bearer ${localStorage.getItem(TOKEN_KEY)}`,
  },
});

export const HoldingsReportPage: React.FC<IResourceComponentsProps> = () => {
  const [isGenerated, setIsGenerated] = React.useState(false);
  const [generating, setGenerating] = React.useState(false);
  const [vesselDetails, setVesselDetails] = React.useState<{
    name: string;
    imoNumber: string;
    ukhoLicenceId: string;
  } | null>(null);

  const { list } = useNavigation();
  const { open } = useNotification();
  const apiUrl = useApiUrl();

  const {
    current,
    gotoStep,
    stepsProps,
    formProps,
    formLoading: isLoading,
  } = useStepsForm<IVesselResource>({
    resource: "vessel-resource",
    redirect: false,
  });

  const { selectProps: vesselSelectProps } = useSelect<IVessel>({
    resource: "vessel",
    optionLabel: "name",
    optionValue: "imoNumber",

    pagination: {
      mode: "server"
    }
  });

  const getSelectedVesselName = (): string => {
    const imoNumber = formProps.form?.getFieldValue(["vessel", "imoNumber"]);
    if (!imoNumber) return "";

    const vessels = vesselSelectProps.options || [];
    const vessel = vessels.find((v: any) => v.value === imoNumber);
    return String(vessel?.label || "");
  };

  const fetchVesselDetails = async (imoNumber: string) => {
    try {
      const response = await axios.get(
        `${apiUrl}/vessel?filter=imoNumber||$eq||${imoNumber}&join=licence`,
        getAuthHeaders()
      );

      // Handle both array and paginated response formats
      const vessels = Array.isArray(response.data) ? response.data : response.data?.data;
      const vessel = vessels?.[0];

      if (vessel) {
        const ukhoLicenceId = vessel.licence?.ukhoLicenceId || vessel.ukhoLicenceId;
        if (ukhoLicenceId) {
          setVesselDetails({
            name: vessel.name,
            imoNumber: vessel.imoNumber,
            ukhoLicenceId: ukhoLicenceId,
          });
          return ukhoLicenceId;
        }
      }
      throw new Error("Vessel or licence not found");
    } catch (error: any) {
      console.error("fetchVesselDetails error:", error);
      open?.({
        type: "error",
        message: error?.message || "Failed to fetch vessel details",
      });
      return null;
    }
  };

  const handleVesselChange = async (imoNumber: string) => {
    if (imoNumber) {
      await fetchVesselDetails(imoNumber);
    } else {
      setVesselDetails(null);
    }
  };

  const handleGenerateReport = async () => {
    const imoNumber = formProps.form?.getFieldValue(["vessel", "imoNumber"]);

    if (!imoNumber) {
      open?.({
        type: "error",
        message: "Please select a vessel first",
      });
      return;
    }

    setGenerating(true);

    try {
      // Ensure we have vessel details
      let ukhoLicenceId = vesselDetails?.ukhoLicenceId;
      if (!ukhoLicenceId) {
        ukhoLicenceId = await fetchVesselDetails(imoNumber);
        if (!ukhoLicenceId) {
          setGenerating(false);
          return;
        }
      }

      // Fetch holdings report
      const response = await axios.get(
        `${apiUrl}/vessel-resource/holdings-report/${ukhoLicenceId}`,
        getAuthHeaders()
      );

      // Handle different response codes
      if (response.status === 204) {
        open?.({
          type: "error",
          message: "This licence has no AVCS holdings",
        });
        setGenerating(false);
        return;
      }

      const holdings: IHoldingsReportEntry[] = response.data;

      if (!holdings || holdings.length === 0) {
        open?.({
          type: "error",
          message: "No holdings data found for this vessel",
        });
        setGenerating(false);
        return;
      }

      // Generate PDF
      const pdfBlob = await generateHoldingsPdf({
        holdings,
        vesselName: vesselDetails?.name || getSelectedVesselName(),
        imoNumber,
        ukhoLicenceId,
      });

      // Download PDF
      const fileName = `Holdings_Report_${imoNumber}_${new Date().toISOString().split("T")[0]}.pdf`;
      FileSaver.saveAs(pdfBlob, fileName);

      setIsGenerated(true);
      open?.({
        type: "success",
        message: "Holdings report generated successfully",
      });
    } catch (error: any) {
      // Handle specific error codes
      const status = error?.response?.status;
      const errorData = error?.response?.data;

      let errorMessage = "Failed to generate holdings report";

      switch (status) {
        case 204:
          errorMessage = "This licence has no AVCS holdings";
          break;
        case 400:
          errorMessage = errorData?.errors?.[0]?.description || "Invalid Licence ID or no AVCS tab";
          break;
        case 401:
          errorMessage = "Authentication failed. Please login again";
          break;
        case 403:
          errorMessage = errorData?.errors?.[0]?.description || "You don't have permission for this licence";
          break;
        case 404:
          errorMessage = errorData?.errors?.[0]?.description || "Licence not found";
          break;
        case 429:
          errorMessage = "Too many requests. Please try again shortly";
          break;
        case 500:
        case 503:
          errorMessage = "Server error. Please try again later";
          break;
        default:
          errorMessage = error?.message || "Failed to generate holdings report";
      }

      open?.({
        type: "error",
        message: errorMessage,
      });
    } finally {
      setGenerating(false);
    }
  };

  const formList = [
    <>
      <Form.Item
        label="Vessel"
        name={["vessel", "imoNumber"]}
        rules={[
          {
            required: true,
          },
        ]}
      >
        <Select
          {...vesselSelectProps}
          onChange={(value: any) => handleVesselChange(String(value || ""))}
        />
      </Form.Item>
    </>,
    <>
      <Alert
        message="Holdings Report Generation"
        description={`You are about to generate an AVCS Holdings Report for ${getSelectedVesselName()}. This will download a PDF document containing all AVCS chart holdings for this vessel.`}
        type="info"
        showIcon
        style={{ marginBottom: 24 }}
      />

      {/* {vesselDetails && (
        <Alert
          message="Vessel Details"
          description={
            <div>
              <p><strong>Vessel:</strong> {vesselDetails.name}</p>
              <p><strong>IMO:</strong> {vesselDetails.imoNumber}</p>
              <p><strong>Licence ID:</strong> {vesselDetails.ukhoLicenceId}</p>
            </div>
          }
          type="success"
          showIcon
          style={{ marginBottom: 24 }}
        />
      )} */}

      {isGenerated ? (
        <Result
          status="success"
          title="Holdings Report Generated"
          subTitle={<strong>The PDF has been downloaded to your device.</strong>}
        />
      ) : (
        <div style={{ display: "flex", justifyContent: "center", marginTop: 16 }}>
          <Button
            type="primary"
            onClick={handleGenerateReport}
            loading={generating}
            icon={<Icons.FilePdfOutlined />}
            size="large"
          >
            Generate Holdings Report
          </Button>
        </div>
      )}
    </>,
  ];

  return (
    <Create
      breadcrumb={false}
      headerProps={{
        onBack: () => list("vessel-resource"),
        title: "Holdings Report",
        subTitle: "generate AVCS holdings PDF",
        tags: isLoading ? (
          <Tag color="orange">loading</Tag>
        ) : (
          <Tag color="green">live</Tag>
        ),
        ghost: true,
        extra: <></>,
        footer: (
          <Divider plain>
            © {new Date().getFullYear()} e-Mariner NavSolutions
          </Divider>
        ),
      }}
      saveButtonProps={{
        children: "Go to List",
        icon: <Icons.UnorderedListOutlined />,
        onClick: () => list("vessel-resource"),
        style: { display: isGenerated ? "block" : "none" },
      }}
    >
      <Steps {...stepsProps}>
        <Steps.Step title="Select Vessel" />
        <Steps.Step title="Generate Report" />
      </Steps>
      <div>
        <Form {...formProps} layout="vertical" style={{ paddingTop: 20 }}>
          {formList[current]}
        </Form>
        <>
          {current > 0 && (
            <div
              style={{
                paddingTop: 20,
              }}
            >
              <Button
                onClick={() => {
                  gotoStep(current - 1);
                }}
                disabled={generating}
              >
                Previous
              </Button>
            </div>
          )}
          {current < formList.length - 1 && (
            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
              }}
            >
              <Button
                onClick={() => {
                  gotoStep(current + 1);
                }}
              >
                Next
              </Button>
            </div>
          )}
        </>
      </div>
    </Create>
  );
};
